/**
 * The shipped database: data/*.json compiled into the bundle (its own `food-db` chunk, see
 * vite.config.ts), so the service worker precaches it as ordinary app code and there is no
 * runtime fetch to miss offline.
 *
 * TOLERANT ON PURPOSE. `import.meta.glob` matching nothing yields `{}`, so a missing data file
 * is a smaller catalog, never a broken build. Each file may be a bare array (or table) or an
 * object wrapping one under its own name (`{ "foods": [...] }`), and rows that do not look like
 * the current model are dropped rather than trusted. INTEGRATOR: if the data formats settle on
 * something else, `parseBundled` is the one function to change.
 *
 * TRIPS NEVER SHIP (rule 7). Only top-level data/*.json is globbed; data/trips/ is not, and
 * the test guards it.
 */
import type {
  AvailabilityRating,
  AvailabilityTable,
  Category,
  Food,
  Recipe,
  Region,
} from '@resupport/core'

export interface BundledData {
  categories: Category[]
  foods: Food[]
  recipes: Recipe[]
  availability: AvailabilityTable
  regions: Region[]
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)
const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

export function isCategoryLike(v: unknown): v is Category {
  return isObj(v) && isStr(v.id) && isStr(v.name) && (v.parent === null || isStr(v.parent))
}

export function isFoodLike(v: unknown): v is Food {
  return isObj(v) && isStr(v.id) && isStr(v.name) && isStr(v.category) && isNum(v.kcal_per_100g)
}

export function isRecipeLike(v: unknown): v is Recipe {
  return (
    isObj(v) &&
    isStr(v.id) &&
    isStr(v.name) &&
    Array.isArray(v.ingredients) &&
    v.ingredients.every((i) => isObj(i) && Array.isArray(i.options) && isNum(i.g))
  )
}

export function isRegionLike(v: unknown): v is Region {
  return isObj(v) && isStr(v.code) && isStr(v.name) && isStr(v.currency)
}

function isRatingLike(v: unknown): v is AvailabilityRating {
  return isObj(v) && isNum(v.level) && v.level >= 0 && v.level <= 5
}

/** `[...]` or `{ key: [...] }`. */
function rows(value: unknown, key: string): unknown[] {
  if (Array.isArray(value)) return value
  if (isObj(value) && Array.isArray(value[key])) return value[key] as unknown[]
  return []
}

function table(value: unknown): AvailabilityTable {
  const src = isObj(value) && isObj(value.availability) ? value.availability : value
  const out: AvailabilityTable = {}
  if (!isObj(src)) return out
  for (const [category, byRegion] of Object.entries(src)) {
    if (!isObj(byRegion)) continue
    const regions: Record<string, AvailabilityRating> = {}
    for (const [region, rating] of Object.entries(byRegion)) {
      if (isRatingLike(rating)) regions[region] = { ...rating, source: rating.source ?? 'curated' }
    }
    if (Object.keys(regions).length > 0) out[category] = regions
  }
  return out
}

/**
 * A handful of regions so the region picker and the energy/currency defaults work before
 * data/regions.json exists. The shipped file wins by code.
 */
export const FALLBACK_REGIONS: Region[] = [
  { code: 'AU', name: 'Australia', currency: 'AUD', label_energy: 'kJ' },
  { code: 'NZ', name: 'New Zealand', currency: 'NZD', label_energy: 'kJ' },
  { code: 'US', name: 'United States', currency: 'USD', label_energy: 'kcal' },
  { code: 'CA', name: 'Canada', currency: 'CAD', label_energy: 'kcal' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', label_energy: 'both' },
  { code: 'IE', name: 'Ireland', currency: 'EUR', label_energy: 'both' },
  { code: 'ES', name: 'Spain', currency: 'EUR', label_energy: 'both' },
  { code: 'PT', name: 'Portugal', currency: 'EUR', label_energy: 'both' },
  { code: 'FR', name: 'France', currency: 'EUR', label_energy: 'both' },
  { code: 'DE', name: 'Germany', currency: 'EUR', label_energy: 'both' },
  { code: 'IT', name: 'Italy', currency: 'EUR', label_energy: 'both' },
  { code: 'CH', name: 'Switzerland', currency: 'CHF', label_energy: 'both' },
  { code: 'JP', name: 'Japan', currency: 'JPY', label_energy: 'kcal' },
  { code: 'ZA', name: 'South Africa', currency: 'ZAR', label_energy: 'kJ' },
]

function byKey<T>(items: T[], key: (t: T) => string): T[] {
  const out = new Map<string, T>()
  for (const item of items) out.set(key(item), item)
  return [...out.values()]
}

/**
 * Glob result (path → module default) to a BundledData. Pure: tested without Vite.
 * Matches by file name, so it does not care where data/ is relative to the caller.
 */
export function parseBundled(files: Record<string, unknown>): BundledData {
  const file = (name: string): unknown => {
    for (const [path, value] of Object.entries(files)) {
      if (path.endsWith(`/${name}`) && !path.includes('/trips/')) return value
    }
    return undefined
  }
  return {
    categories: byKey(
      rows(file('categories.json'), 'categories').filter(isCategoryLike),
      (c) => c.id,
    ),
    foods: byKey(rows(file('foods.json'), 'foods').filter(isFoodLike), (f) => f.id),
    recipes: byKey(rows(file('recipes.json'), 'recipes').filter(isRecipeLike), (r) => r.id),
    availability: table(file('availability.json')),
    regions: byKey(
      [...FALLBACK_REGIONS, ...rows(file('regions.json'), 'regions').filter(isRegionLike)],
      (r) => r.code,
    ),
  }
}

const dataFiles = import.meta.glob<unknown>('../../../data/*.json', {
  eager: true,
  import: 'default',
})

export const BUNDLED: BundledData = parseBundled(dataFiles)
